import React, { useState, useEffect } from 'react';
import { saveAs } from 'file-saver';
import uniqid from 'uniqid';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Loader } from 'ui/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import Button from 'components/partials/button/button';
import RelatedContent from 'containers/user/learn/components/related-content/related-content';

import api from 'api';

import { useCurrentUser } from 'hooks/api/use-current-user';

import {
  getIsRelatedContentShown,
  getRelatedContent,
} from 'containers/user/learn/heplers/content-builder-helpers';
import MarkdownComponent from 'containers/user/learn/components/markdown/markdown';
import { getBlocksFromContent } from 'containers/user/learn/heplers/get-blocks-from-content';

import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import { Typography } from 'ui/typography';

import { formatDate } from 'helpers/dates-formatter';
import Icons from 'assets/icons';

interface DownloadFileEntry {
  attributes?: { name?: string; url?: string };
}

interface DownloadData {
  name?: string;
  heroDescription?: string;
  downloadText?: string;
  downloadBtnText?: string;
  assetCreationDate?: string;
  heroImage?: { data?: { attributes?: { url?: string } } };
  downloadFile?: { data: DownloadFileEntry[] };
  content?: Array<{
    id: string | number;
    type: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

interface DownloadProps {
  downloadId: string | number;
}

const Download = ({ downloadId }: DownloadProps): React.JSX.Element => {
  const [download, setDownload] = useState<DownloadData>({});
  const [loading, setLoading] = useState<boolean>(false);

  const user = useCurrentUser();

  const dowloadFile = (): void => {
    setLoading(true);
    const fileName = download?.downloadFile?.data[0]?.attributes?.name;
    const fileUrl = download?.downloadFile?.data[0]?.attributes?.url;

    saveAs(fileUrl as string, fileName);

    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    api
      .getDownload(downloadId, user?.data?.oktaUserId ?? '')
      .then((res) => {
        setDownload(
          (res as { data: { data: { attributes: DownloadData } } }).data.data
            .attributes
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const isDatePresented = !!download?.assetCreationDate;

  return !!loading ? (
    <Loader {...{ loading }} />
  ) : (
    <Stack sx={{ justifyContent: 'center', width: '100%' }}>
      <Stack
        direction="row"
        sx={{ justifyContent: 'center', width: '100%', height: 'auto' }}
      >
        <ResponsiveWrapper customClass="flex flex-col justify-center !w-auto">
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Learn', href: '/learn' },
              { label: 'Downloads', href: '/learn/content/downloads' },
            ]}
            customClass="!mt-5"
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 3,
              '@media (min-width: 375px)': { flexDirection: 'column', py: 2 },
              '@media (min-width: 780px)': { flexDirection: 'row', py: 5 },
            }}
          >
            <Stack>
              <Typography
                data-testid="download-heading"
                variant="h1"
                sx={[
                  { mb: 1 },
                  !isDatePresented && { mb: { mobile: 3, xs: 4 } },
                ]}
              >
                {download?.name}
              </Typography>

              {isDatePresented && (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    color: 'tokens.navy.500',
                    '@media (min-width: 375px)': { mb: 3 },
                    '@media (min-width: 960px)': { mb: 4 },
                  }}
                >
                  <Icons.Calendar className="h-4 w-4 mr-1" />
                  <Box sx={{ fontSize: '0.875rem', lineHeight: '1.25rem' }}>
                    {formatDate(
                      download.assetCreationDate as string,
                      'MMM D, YYYY'
                    )}
                  </Box>
                </Stack>
              )}

              <MarkdownComponent text={download?.heroDescription} />

              <span className="font-large font-semibold mobile:mt-6 md:mt-10">
                {download?.downloadText}
              </span>
              <Button
                variant="brand-filled"
                size="sm"
                iconAfter={<Icons.Download />}
                className="mt-3 w-fit"
                onClick={() => dowloadFile()}
              >
                {download?.downloadBtnText}
              </Button>
            </Stack>
            {download?.heroImage && (
              <img
                src={download?.heroImage?.data?.attributes?.url}
                alt="Hero Img"
                className="flex h-auto tablet:w-1/2 xs:w-1/3 justify-center"
              />
            )}
          </Box>
        </ResponsiveWrapper>
      </Stack>
      {!!download.content?.length && (
        <Stack direction="row" sx={{ justifyContent: 'center', width: '100%' }}>
          <ResponsiveWrapper customClass="flex flex-col justify-center my-3">
            {download.content.map((component) =>
              getBlocksFromContent({ ...component, key: uniqid() })
            )}
            {getIsRelatedContentShown(
              download as never,
              'downloadIsLinkedTo'
            ) && (
              <RelatedContent
                content={
                  getRelatedContent(
                    download as never,
                    'downloadIsLinkedTo'
                  ) as never
                }
              />
            )}
          </ResponsiveWrapper>
        </Stack>
      )}
    </Stack>
  );
};

export default Download;
